import { Box, Container, Divider, Flex, Stack } from "@chakra-ui/react"
import { AxiosError, AxiosResponse } from "axios"
import { Formik, useFormik } from "formik"
import React, {
  ReactNode,
  Reducer,
  useContext,
  useEffect,
  useReducer,
  useState
} from "react"
import { useHistory } from "react-router-dom"
import HomeBackIcon from "src/components/views/home/HomeBackIcon"
import { createHost } from "src/conditionals/Authentication"
import { Routes } from "src/constants"
import { SDKContext } from "src/context/SDKContext"
import useConfig from "src/hooks/useConfig"
import useAuthedUser from "src/hooks/useAuthedUser"
import usePageStyling from "src/hooks/usePageStyling"
import useSearchParams from "src/hooks/useSearchParams"
import useWindowSize from "src/hooks/useWindowSize"
import axios from "src/shared/axios"
import { useIsMobileLandscape } from "src/util/browser"
import { useIsIOSApp, useIsMobileApp } from "src/util/deviceType"
import FormText from "../formText"
import { OAuthLogin } from "../oauth-login"
import { ConditionalAppLogo } from "src/conditionals/Authentication/components/ConditionalAppLogo"
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from "react-google-recaptcha-v3"

type ReturnType<T> = T extends (...args: any[]) => infer R ? R : any
type FormikHandleChange = ReturnType<typeof useFormik>["handleChange"]
type FormikHandleBlur = ReturnType<typeof useFormik>["handleBlur"]

type FormikValues = {
  email: string
  verifyCode: string
  password: string
  passwordConfirm: string
  recaptchaToken?: string
}

export type RenderPropsArgs = {
  values: FormikValues
  errors: Partial<FormikValues>
  handleChange: FormikHandleChange
  handleBlur: FormikHandleBlur
}

type Props = {
  logo: ReactNode
  heading: ReactNode
  navigationPanel: ReactNode
  type: "register" | "login" | "verify" | "reset"
  instructionText?: ReactNode
  hasOAuth?: boolean
  extraRequestBodyProps?: Record<string, string | null>
  redirectToUrl?: string
  successMessage?: (email: string) => ReactNode
  emailInput?: (args: RenderPropsArgs) => ReactNode
  passwordInput?: (args: RenderPropsArgs) => ReactNode
  passwordConfirmInput?: (args: RenderPropsArgs) => ReactNode
  codeInput?: (args: RenderPropsArgs) => ReactNode
  submitButton: (args: { isSubmitting: boolean }) => ReactNode
  onSubmitRequest: (args: FormikValues) => Promise<AxiosResponse<any>>
}

type AuthFormState = {
  responseError?: string
  showSuccessMessage: boolean
}

type AuthFormAction =
  | {
      type: "submit_success"
    }
  | { type: "submit_failure"; responseError?: string }
  | { type: "reset" }

function reducer(state: AuthFormState, action: AuthFormAction) {
  switch (action.type) {
    case "submit_success":
      return { ...state, showSuccessMessage: true }
    case "submit_failure":
      return { ...state, responseError: action.responseError }
    case "reset":
      return initialReducerState()
  }
}

function initialReducerState() {
  return { showSuccessMessage: false, responseError: undefined }
}

function AuthFormInner({
  heading,
  logo,
  type,
  redirectToUrl,
  successMessage,
  instructionText = null,
  hasOAuth = false,
  navigationPanel,
  emailInput,
  codeInput,
  passwordInput,
  passwordConfirmInput,
  submitButton,
  onSubmitRequest
}: Props) {
  const config = useConfig()
  const meetingClient = useContext(SDKContext)
  const roomStyles = usePageStyling("authPageStyling")
  const history = useHistory()
  const isMobileLandscape = useIsMobileLandscape()
  const { authedUser, getAuthedUser } = useAuthedUser()

  const [{ responseError, showSuccessMessage }, dispatch] = useReducer<
    Reducer<AuthFormState, AuthFormAction>
  >(reducer, initialReducerState())

  React.useEffect(() => {
    switch (type) {
      case "login":
      case "register":
      case "verify":
        return
      case "reset":
        break
    }
    let url = new URL(window.location.href)
    let params = new URLSearchParams(url.search)
    params.set("isSecureInAppBrowser", "true")
    url.search = params.toString()
    meetingClient?.nativeBridge?.eventOpenPageInSecureBrowser({
      pageURL: url.toString()
    })
  }, [])

  const isMobileApp = useIsMobileApp()
  const isIOSApp = useIsIOSApp()
  const { isSecureInAppBrowser } = useSearchParams<{
    isSecureInAppBrowser?: true | undefined
  }>()
  // const isSecureInAppBrowser = true // debug
  const isWKWebView = isIOSApp && !isSecureInAppBrowser
  const showEmailAndPasswordButton =
    (type === "login" || type === "register") && isWKWebView

  const { screenSize } = useWindowSize()
  const showBackButtonInsideForm =
    (screenSize.width < 430 || screenSize.height < 730) &&
    isMobileApp &&
    !isSecureInAppBrowser

  return (
    <>
      <Stack spacing={"0.4rem"} py="5px">
        <Flex justifyContent={"center"}>
          {!isMobileLandscape && (
            <Box flexGrow={1} marginLeft="-14px" marginTop="-5px">
              <ConditionalAppLogo showLogo />
            </Box>
          )}
          <Stack align={"center"}>{heading}</Stack>
          {/* Note: this second logo is not shown intentionally. It's just used to balance the other side and ensure the heading is centered. */}
          {!isMobileLandscape && (
            <Box flexGrow={1} visibility="hidden" marginRight="-14px">
              <ConditionalAppLogo showLogo />
            </Box>
          )}
        </Flex>
        {config.learnmachineAuthPage !== "loading" && hasOAuth && (
          <OAuthLogin
            oAuthRedirectURI={config.oauthRedirectUri}
            googleOAuthId={config.googleOauthId}
            appleOAuthId={config.appleOauthClientId}
            authPageStyling={roomStyles}
            darkmode={config.learnmachineAuthPage}
            displayAsRow={isMobileLandscape}
            showEmailAndPasswordButton={showEmailAndPasswordButton}
          />
        )}
        {instructionText}
        <Formik
          initialValues={{
            email: "",
            password: "",
            passwordConfirm: "",
            verifyCode: ""
          }}
          enableReinitialize={true}
          validate={values => {
            const errors: Partial<FormikValues> = {}
            if (!values.email && !!emailInput) {
              errors.email = "Required"
            } else if (
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email) &&
              !!emailInput
            ) {
              errors.email = "Invalid email address"
            }

            if (!values.password && !!passwordInput) {
              errors.password = "Required"
            } else if (values.password.length < 8 && !!passwordInput) {
              errors.password = "At least 8 characters"
            }

            if (
              values.password !== values.passwordConfirm &&
              !!passwordConfirmInput
            ) {
              errors.passwordConfirm = "Does not match"
            }

            if (!values.verifyCode && !!codeInput) {
              errors.verifyCode = "Required"
            } else if (!/^\d{6}$/.test(values.verifyCode) && !!codeInput) {
              errors.verifyCode = "Invalid code"
            }
            return errors
          }}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={async (values, { setSubmitting, setErrors }) => {
            if (!meetingClient) throw "Meeting client not initialised"
            setSubmitting(true)
            try {
              const res = await onSubmitRequest(values)

              if (res.status == 200 && redirectToUrl) {
                const user = await getAuthedUser()
                const obtainRedirectURL = () =>
                  user.verified ? redirectToUrl : Routes.VERIFY

                if (isSecureInAppBrowser) {
                  const res = await axios.get(
                    meetingClient?.envConfig.ONE_TIME_CODE,
                    {
                      withCredentials: true,
                      headers: {
                        "Content-Type": "application/json"
                      }
                    }
                  )
                  const code = res.data.code
                  // this navigation will get intercepted by deep linking, telling the native app to close the secure safari browser and load this url in WKWebView instead to consume the one-time-code
                  // document.location = `https://utta.app/one-time-code?code=${code}&host=${createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)}&redirectTo=${obtainRedirectURL()}`
                  // we have to use a custom url scheme to get the link to trigger inside of embedded safari
                  document.location = `${
                    config.iosAppUrlScheme
                  }://one-time-code?code=${code}&host=${createHost(
                    meetingClient?.envConfig.DOMAIN_HOSTNAME
                  )}&redirectTo=${obtainRedirectURL()}`
                  return
                }
                localStorage.setItem("avatarsTabIndex", JSON.stringify(0))

                history.replace(obtainRedirectURL())
              } else if (res.status === 200 && successMessage) {
                dispatch({ type: "submit_success" })
              }
            } catch (error: unknown) {
              const err = error as AxiosError
              if (err.response?.status === 422 && !!values.verifyCode.length) {
                setErrors({ verifyCode: "Incorrect code" })
              } else if (err.response?.status === 422) {
                setSubmitting(false)
                dispatch({
                  type: "submit_failure",
                  responseError: "Email or Password incorrect."
                })
                return
              } else if (err.response?.status === 403) {
                setSubmitting(false)
                dispatch({
                  type: "submit_failure",
                  responseError: "Registration failed. Please try again."
                })
                return
              } else {
                setSubmitting(false)
                dispatch({
                  type: "submit_failure",
                  responseError: "Something went wrong."
                })
                return
              }
            }

            setSubmitting(false)
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setValues,
            setErrors,
            initialValues
          }) => {
            useEffect(() => {
              // reset values whenever authform version is changed
              setValues(initialValues)
              setErrors({})
              dispatch({ type: "reset" })
            }, [type, setValues])

            return (
              <form noValidate onSubmit={handleSubmit}>
                <Box
                  display="flex"
                  flexDir={isMobileLandscape ? "row" : "column"}
                  gap="1.5"
                >
                  {!!emailInput &&
                    !showEmailAndPasswordButton &&
                    emailInput({
                      errors,
                      values,
                      handleBlur,
                      handleChange
                    })}
                  {!!codeInput &&
                    codeInput({
                      errors,
                      values,
                      handleBlur,
                      handleChange
                    })}
                  {!!passwordInput &&
                    !showEmailAndPasswordButton &&
                    passwordInput({
                      errors,
                      values,
                      handleBlur,
                      handleChange
                    })}
                  {!!passwordConfirmInput &&
                    !showEmailAndPasswordButton &&
                    passwordConfirmInput({
                      errors,
                      values,
                      handleBlur,
                      handleChange
                    })}
                </Box>
                {!!responseError?.length && (
                  <FormText
                    color={roomStyles?.authPageWarningColor ?? "red"}
                    fontSize="13px"
                  >
                    {responseError}
                  </FormText>
                )}
                {showSuccessMessage && successMessage && (
                  <>{successMessage(values.email)}</>
                )}
                {!showEmailAndPasswordButton && submitButton({ isSubmitting })}
                {showEmailAndPasswordButton && (
                  <div style={{ marginTop: "18px" }} />
                )}
                {navigationPanel}
                {type === "register" && (
                  <FormText textAlign="center" fontSize="12px">
                    By continuing, you agree to our Terms and acknowledge our
                    Privacy Policy.
                  </FormText>
                )}
              </form>
            )
          }}
        </Formik>
      </Stack>
    </>
  )
}

function RecaptchaAuthForm(props: Props) {
  const { executeRecaptcha } = useGoogleReCaptcha()

  const wrappedOnSubmitRequest = async (values: FormikValues) => {
    let recaptchaToken: string | undefined

    if (executeRecaptcha) {
      try {
        recaptchaToken = await executeRecaptcha("register")
      } catch (error) {
        console.error("reCAPTCHA execution failed:", error)
        // Continue without token - backend will reject if required
      }
    } else {
      console.warn("reCAPTCHA not ready yet")
    }

    return props.onSubmitRequest({ ...values, recaptchaToken })
  }

  return <AuthFormInner {...props} onSubmitRequest={wrappedOnSubmitRequest} />
}

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

function AuthForm(props: Props) {
  if (props.type === "register" && RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        container={{
          parameters: {
            badge: "bottomright"
          }
        }}
      >
        <RecaptchaAuthForm {...props} />
      </GoogleReCaptchaProvider>
    )
  }

  return <AuthFormInner {...props} />
}

export default AuthForm
